import { DeleteOutlined } from '@ant-design/icons';
import { Conversations, ConversationsProps } from '@ant-design/x';
import { AddIcon, BalanceAIColoredIcon } from 'common/icons';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { MediaQuery } from 'src/theme/media-query';

import { CropwiseAiTabsEnum } from '../cropwise-ai.enum';
import { CropwiseAiHistoryMenuKey } from './cropwise-ai-history.constants';
import { useCropwiseAiHistory } from './cropwise-ai-history.hook';
import * as Styled from './cropwise-ai-history.style';

interface CropwiseAiHistoryProps {
  showType: CropwiseAiTabsEnum;
  sessionId: string | null;
  setSessionId: Dispatch<SetStateAction<string | null>>;
  setHistoryOpen?: Dispatch<SetStateAction<boolean>>;
  historyVersion: number;
}

const CropwiseAiHistory = ({
  showType,
  sessionId,
  setSessionId,
  setHistoryOpen,
  historyVersion,
}: CropwiseAiHistoryProps) => {
  const {
    items,
    conversationHistory,
    handleDelete,
    handleNewConversation,
    handleOnClickSelectConversation,
  } = useCropwiseAiHistory({
    sessionId,
    setSessionId,
    setHistoryOpen,
    historyVersion,
  });
  const { t } = useTranslation();
  const isFullscreen = showType === CropwiseAiTabsEnum.FULL_SCREEN;
  const isHidden = showType === CropwiseAiTabsEnum.HIDE;
  const isDesktop = useMediaQuery({ minWidth: MediaQuery.screenSizes.desktop });

  if (isHidden) {
    return null;
  }

  const menuConfig: ConversationsProps['menu'] = (conversation) => ({
    items: [
      {
        label: t('cropwise_ai.delete_chat'),
        key: CropwiseAiHistoryMenuKey.DELETE_CHAT,
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => {
          void handleDelete(conversation.key);
        },
      },
    ],
  });

  return (
    <Styled.Container>
      {isFullscreen && isDesktop && (
        <Styled.TitleContainer>
          <BalanceAIColoredIcon />
          {t('cropwise_ai.title')}
        </Styled.TitleContainer>
      )}
      <Styled.NewButton onClick={handleNewConversation} icon={<AddIcon />}>
        {t('cropwise_ai.new_conversation')}
      </Styled.NewButton>
      <Conversations
        defaultActiveKey={conversationHistory?.[0]?.sessionId}
        menu={menuConfig}
        items={items}
        activeKey={sessionId ?? ''}
        onActiveChange={handleOnClickSelectConversation}
        styles={{
          item: {
            padding: '10px 12px',
          },
          root: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
        groupable
      />
    </Styled.Container>
  );
};

export { CropwiseAiHistory };
